import { jsPDF } from 'jspdf';
import 'svg2pdf.js';

// Vector-first exports. PDF is true vector (svg2pdf + embedded Barlow
// Condensed), SVG embeds fonts + images so it opens anywhere, PNG is a
// 300 dpi raster of the same SVG.
const FONT_FILES = [[400, 'BarlowCondensed-Regular.ttf'], [600, 'BarlowCondensed-SemiBold.ttf'], [700, 'BarlowCondensed-Bold.ttf']];
const NS = 'http://www.w3.org/2000/svg';
let fontsPromise = null;
const imgCache = new Map();

function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function loadFonts() {
  if (!fontsPromise) {
    fontsPromise = Promise.all(FONT_FILES.map(async ([weight, file]) => {
      const res = await fetch(`/fonts/${file}`);
      return { weight, file, b64: toB64(await res.arrayBuffer()) };
    }));
    fontsPromise.catch(() => { fontsPromise = null; });
  }
  return fontsPromise;
}

async function toPngDataUrl(href) {
  if (!href || href.startsWith('data:')) return href;
  if (!imgCache.has(href)) {
    imgCache.set(href, (async () => {
      const blob = await (await fetch(href)).blob();
      const bmp = await createImageBitmap(blob);
      const w = Math.min(256, bmp.width);
      const c = document.createElement('canvas');
      c.width = w;
      c.height = Math.round((bmp.height / bmp.width) * w);
      c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
      return c.toDataURL('image/png');
    })());
  }
  return imgCache.get(href);
}

async function prepare(svgEl, page) {
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('xmlns', NS);
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  clone.setAttribute('width', `${page.wIn}in`);
  clone.setAttribute('height', `${page.hIn}in`);
  clone.removeAttribute('style');
  clone.querySelectorAll('[data-screen-only]').forEach((n) => n.remove());
  await Promise.all([...clone.querySelectorAll('image')].map(async (im) => {
    try {
      const data = await toPngDataUrl(im.getAttribute('href'));
      im.setAttribute('href', data);
      im.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', data);
    } catch { im.remove(); }
  }));
  return clone;
}

async function standaloneSvg(svgEl, page) {
  const [clone, fonts] = await Promise.all([prepare(svgEl, page), loadFonts()]);
  const style = document.createElementNS(NS, 'style');
  style.textContent = fonts.map((f) => `@font-face{font-family:'Barlow Condensed';font-weight:${f.weight};src:url(data:font/ttf;base64,${f.b64}) format('truetype');}`).join('');
  clone.insertBefore(style, clone.firstChild);
  return new XMLSerializer().serializeToString(clone);
}

function download(blob, name) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

const suffix = (i, n) => (n > 1 ? `-page-${i + 1}` : '');
const pause = (ms) => new Promise((r) => { setTimeout(r, ms); });

export async function exportPdf(svgEls, page, name) {
  const fonts = await loadFonts();
  const orient = page.w > page.h ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation: orient, unit: 'pt', format: [page.w, page.h] });
  fonts.forEach((f) => { doc.addFileToVFS(f.file, f.b64); doc.addFont(f.file, 'Barlow Condensed', 'normal', f.weight); });
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-100000px;top:0;width:0;height:0;overflow:hidden;';
  document.body.appendChild(host);
  try {
    for (let i = 0; i < svgEls.length; i += 1) {
      if (i) doc.addPage([page.w, page.h], orient);
      const clone = await prepare(svgEls[i], page);
      host.appendChild(clone);
      await doc.svg(clone, { x: 0, y: 0, width: page.w, height: page.h });
      clone.remove();
    }
  } finally { host.remove(); }
  doc.setProperties({ title: name, creator: 'Schedule Guru' });
  doc.save(`${name}.pdf`);
}

export async function exportSvg(svgEls, page, name) {
  for (let i = 0; i < svgEls.length; i += 1) {
    const xml = await standaloneSvg(svgEls[i], page);
    download(new Blob([xml], { type: 'image/svg+xml' }), `${name}${suffix(i, svgEls.length)}.svg`);
    await pause(250);
  }
}

export async function exportPng(svgEls, page, name, dpi = 300) {
  for (let i = 0; i < svgEls.length; i += 1) {
    const xml = await standaloneSvg(svgEls[i], page);
    const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml' }));
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      const c = document.createElement('canvas');
      c.width = Math.round(page.wIn * dpi);
      c.height = Math.round(page.hIn * dpi);
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const blob = await new Promise((r) => c.toBlob(r, 'image/png'));
      download(blob, `${name}${suffix(i, svgEls.length)}.png`);
    } finally { URL.revokeObjectURL(url); }
    await pause(250);
  }
}
